import { useEffect, useState } from 'react'
import { jsonrpc } from '~/shared/api/jsonrpc'
import { Box, Stat, StatLabel, StatNumber } from '~/shared/ui'

type TokensCount = {
    total?: number
    linked?: number
    burnt?: number
    by_type?: Record<string, number>
}

// `nft.token` holds four collections, not one, and the weapons outnumber the
// operatives several times over -- `nft.0009` loaded 15,000 of them in a single
// migration. So the headline reads as roughly four times the ape-and-droid
// supply anyone has in their head, and the tile gave no way to tell whether that
// was a real number or a broken query. The breakdown is what answers that.
const TYPE_LABELS: Record<string, string> = {
    ape: 'apes',
    droid: 'droids',
    weapon: 'weapons',
    lootbox: 'boxes',
    other: 'unclassified',
}

export const NftTokensCount = () => {
    const [data, setData] = useState<TokensCount>({})

    const getData = () => {
        jsonrpc('statistic.nft.tokens_count').then((res) => setData(res.result || {}))
    }

    useEffect(() => getData(), [])

    const { total, linked, burnt, by_type } = data

    // Both new fields are read defensively rather than assumed. The dashboard's
    // answers are cached for fifteen minutes, so for the first quarter of an
    // hour after this ships the endpoint is still serving pre-existing entries
    // in the old two-key shape.
    const breakdown = Object.entries(by_type || {}).filter(([, count]) => count > 0)

    return (
        <Box className='w-full text-center'>
            <Stat.Gray className='w-full' loading={total === undefined}>
                <StatLabel className='text-amber-500'>NFTs (linked)</StatLabel>
                <StatNumber>
                    {total?.toLocaleString()} ({linked?.toLocaleString()})
                </StatNumber>
                {breakdown.length > 0 && (
                    <Box className='flex-row flex-wrap justify-center gap-x-2 gap-y-0 p-0 text-[0.65rem] leading-tight text-white/50'>
                        {breakdown.map(([type, count]) => (
                            <span key={type} className='tabular-nums whitespace-nowrap'>
                                {count.toLocaleString()} {TYPE_LABELS[type] || type}
                            </span>
                        ))}
                    </Box>
                )}
            </Stat.Gray>
            <small className='text-white/50'>Number of minted NFTs (Number of NFTs linked to the website)</small>
            {burnt ? (
                <small className='text-white/30'>
                    Includes {burnt.toLocaleString()} burnt, which stay counted as minted
                </small>
            ) : null}
        </Box>
    )
}
